import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Handle, Position } from 'reactflow';
import { useTheme } from '../../../contexts/ThemeContext';
import * as artifactService from '../../../services/artifactService';
import { getHandleById } from '../workflowDefinitions';
import { WorkflowNodeType } from '../types';

const ArtifactNode = ({ data, selected = false }) => {
  const { darkMode } = useTheme();
  const [isSelectingArtifacts, setIsSelectingArtifacts] = useState(false);
  const [availableArtifacts, setAvailableArtifacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArtifacts, setSelectedArtifacts] = useState(data.selectedArtifacts || []);
  const isOutput = data.isOutput || false;

  // CRITICAL: Store callback in ref to survive re-renders
  const onUpdateRef = useRef(data.onUpdate);

  // CRITICAL: Store artifact data in ref to survive re-renders
  const artifactDataRef = useRef({
    selectedArtifacts: data.selectedArtifacts || [],
    isOutput: data.isOutput || false
  });

  // Add logging every render
  console.log('🎨 ARTIFACT-NODE-RENDER: Component rendered', {
    id: data.id,
    hasDataOnUpdate: !!data.onUpdate,
    hasRefCallback: !!onUpdateRef.current,
    isSelectingArtifacts,
    dataKeys: Object.keys(data),
    refSelectedCount: artifactDataRef.current.selectedArtifacts.length,
    propsSelectedCount: (data.selectedArtifacts || []).length
  });

  // Update callback ref when data changes but preserve it if it becomes undefined
  useEffect(() => {
    console.log('🎨 ARTIFACT-CALLBACK-EFFECT: useEffect triggered', {
      hasDataOnUpdate: !!data.onUpdate,
      hasRefCallback: !!onUpdateRef.current,
      onUpdateType: typeof data.onUpdate
    });
    
    if (data.onUpdate && typeof data.onUpdate === 'function') {
      onUpdateRef.current = data.onUpdate;
      console.log('🎨 ARTIFACT-CALLBACK-PRESERVED: Stored callback in ref');
    } else if (!onUpdateRef.current) {
      console.warn('🎨 ARTIFACT-CALLBACK-MISSING: data.onUpdate is not available and no ref callback exists!', {
        dataOnUpdate: data.onUpdate,
        typeOfDataOnUpdate: typeof data.onUpdate,
        fullData: data
      });
    } else {
      console.log('🎨 ARTIFACT-CALLBACK-PRESERVING: data.onUpdate is undefined but ref callback exists, preserving ref');
    }
  }, [data.onUpdate]);

  // Update artifact data ref when we get new data from props
  useEffect(() => {
    if (data.selectedArtifacts && data.selectedArtifacts.length > 0) {
      console.log('🎨 ARTIFACT-DATA-UPDATE: Updating artifact data ref from props', {
        oldSelectedCount: artifactDataRef.current.selectedArtifacts.length,
        newSelectedCount: data.selectedArtifacts.length
      });
      artifactDataRef.current = {
        selectedArtifacts: data.selectedArtifacts,
        isOutput: data.isOutput || false
      };
    }
  }, [data.selectedArtifacts, data.isOutput]);

  // Get the appropriate handle definition
  const handleDef = getHandleById(
    WorkflowNodeType.ARTIFACT, 
    isOutput ? 'artifact_input' : 'artifact_output'
  );

  // Fetch available artifacts when selecting
  useEffect(() => {
    const fetchArtifacts = async () => {
      try {
        setLoading(true);
        setError(null);
        // Use the existing API endpoint to fetch real artifacts
        const artifactsData = await artifactService.getArtifacts();
        setAvailableArtifacts(artifactsData);
      } catch (err) {
        console.error('Error fetching artifacts:', err);
        setError('Failed to load artifacts');
      } finally {
        setLoading(false);
      }
    };

    if (isSelectingArtifacts) {
      fetchArtifacts();
    }
  }, [isSelectingArtifacts]);

  // Filter artifacts based on search term
  const filteredArtifacts = useMemo(() => {
    if (!searchTerm.trim()) return availableArtifacts;
    
    return availableArtifacts.filter(artifact => 
      artifact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (artifact.type && artifact.type.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (artifact.tags && artifact.tags.some(tag => 
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      ))
    );
  }, [availableArtifacts, searchTerm]);

  // Handle artifact selection
  const handleArtifactSelect = (artifactId) => {
    console.log('🎨 ARTIFACT-SELECT-START: About to select/deselect artifact:', artifactId);
    
    const isSelected = selectedArtifacts.some(a => a.id === artifactId);
    
    let updatedArtifacts;
    if (isSelected) {
      // Remove from selection
      updatedArtifacts = selectedArtifacts.filter(a => a.id !== artifactId);
    } else {
      // Add to selection
      const artifactToAdd = availableArtifacts.find(a => a.id === artifactId);
      if (artifactToAdd) {
        updatedArtifacts = [...selectedArtifacts, {
          id: artifactToAdd.id,
          name: artifactToAdd.name,
          type: artifactToAdd.type
        }];
      } else {
        updatedArtifacts = selectedArtifacts;
      }
    }
    
    setSelectedArtifacts(updatedArtifacts);
    
    // Update the artifact data ref immediately to prevent flashing
    console.log('🎨 ARTIFACT-SELECT-UPDATE-REF: Updating artifact data ref immediately', updatedArtifacts);
    artifactDataRef.current = {
      selectedArtifacts: updatedArtifacts,
      isOutput: data.isOutput || false
    };
    
    // Always prioritize ref callback since it's more stable
    const callback = onUpdateRef.current || data.onUpdate;
    
    if (callback && typeof callback === 'function') {
      console.log('🎨 ARTIFACT-SELECT-CALLING: Calling callback (from ref or data)');
      callback(data.id, { 
        selectedArtifacts: updatedArtifacts
      });
    } else {
      console.error('🎨 ARTIFACT-SELECT-ERROR: No callback available!');
      console.error('🎨 ARTIFACT-SELECT-ERROR: data.onUpdate:', data.onUpdate);
      console.error('🎨 ARTIFACT-SELECT-ERROR: onUpdateRef.current:', onUpdateRef.current);
      
      // Try to recover using global debug callback
      if (window.workflowDebugCallback) {
        console.log('🎨 ARTIFACT-SELECT-RECOVERY: Using global debug callback');
        window.workflowDebugCallback('update_artifact', data.id, {
          selectedArtifacts: updatedArtifacts
        });
      } else {
        console.error('🎨 ARTIFACT-SELECT-RECOVERY: No recovery mechanism available');
      }
    }
  };

  // Save selections and close modal
  const handleSaveSelection = () => {
    console.log('🎨 ARTIFACT-SAVE-START: About to save selection');
    
    // Update the artifact data ref immediately
    console.log('🎨 ARTIFACT-SAVE-UPDATE-REF: Updating artifact data ref immediately', selectedArtifacts);
    artifactDataRef.current = {
      selectedArtifacts: selectedArtifacts,
      isOutput: data.isOutput || false
    };
    
    // Always prioritize ref callback since it's more stable
    const callback = onUpdateRef.current || data.onUpdate;
    
    if (callback && typeof callback === 'function') {
      console.log('🎨 ARTIFACT-SAVE-CALLING: Calling callback (from ref or data)');
      callback(data.id, { 
        selectedArtifacts: selectedArtifacts
      });
    } else {
      console.error('🎨 ARTIFACT-SAVE-ERROR: No callback available!');
      console.error('🎨 ARTIFACT-SAVE-ERROR: data.onUpdate:', data.onUpdate);
      console.error('🎨 ARTIFACT-SAVE-ERROR: onUpdateRef.current:', onUpdateRef.current);
      
      // Try to recover using global debug callback
      if (window.workflowDebugCallback) {
        console.log('🎨 ARTIFACT-SAVE-RECOVERY: Using global debug callback');
        window.workflowDebugCallback('update_artifact', data.id, {
          selectedArtifacts: selectedArtifacts
        });
      } else {
        console.error('🎨 ARTIFACT-SAVE-RECOVERY: No recovery mechanism available');
      }
    }
    
    setIsSelectingArtifacts(false);
  };

  return (
    <div className={`
      ${darkMode ? 'bg-gray-700 text-gray-100' : 'bg-white'} 
      border-2 
      ${
        selected 
          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 shadow-lg shadow-amber-500/30 ring-2 ring-amber-300 dark:ring-amber-600' 
          : darkMode ? 'border-purple-600' : 'border-purple-300'
      } 
      rounded-lg p-3 shadow-md min-w-[180px] relative
      transition-all duration-200
      hover:shadow-lg hover:-translate-y-1
    `}>
      {/* Selection indicator badge */}
      {selected && (
        <div className="absolute -top-2 -right-2 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center shadow-lg z-10">
          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      )}
      {/* Position the handle on the top or bottom based on whether it's an input or output artifact */}
      <div className={`absolute ${isOutput ? 'top-0 left-1/2 -translate-y-1/2' : 'bottom-0 left-1/2 translate-y-1/2'} transform -translate-x-1/2`}>
        <Handle
          type={handleDef.type as any}
          position={isOutput ? Position.Top : Position.Bottom}
          id={handleDef.id}
          style={{ background: '#9333ea', width: '8px', height: '8px' }}
          isConnectable={true} // Always make connectable
        />
        <div className={`text-xs font-bold ${darkMode ? 'text-gray-400' : 'text-gray-500'} ${isOutput ? 'mt-1' : 'mb-1'}`}>
          {isOutput ? 'Output' : 'Input'}
        </div>
      </div>
      
      <div className="font-medium mb-2">
        {isOutput ? "Output Artifacts" : "Input Artifacts"}
      </div>
      
      {/* Use ref data for display, fallback to props data */}
      {(artifactDataRef.current.selectedArtifacts.length > 0 || selectedArtifacts.length > 0) ? (
        <div className="mb-2">
          <div className="flex flex-wrap gap-1 mb-2">
            {(artifactDataRef.current.selectedArtifacts.length > 0 ? artifactDataRef.current.selectedArtifacts : selectedArtifacts).map(artifact => (
              <span 
                key={artifact.id} 
                className={`px-2 py-0.5 ${darkMode ? 'bg-purple-900 text-purple-100' : 'bg-purple-100 text-purple-800'} rounded-full text-xs flex items-center`}
              >
                {artifact.name}
                <button 
                  className="ml-1 text-xs"
                  onClick={() => handleArtifactSelect(artifact.id)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <button
            className={`w-full px-2 py-1 text-xs ${darkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white rounded`}
            onClick={() => setIsSelectingArtifacts(true)}
          >
            Manage Artifacts
          </button>
        </div>
      ) : (
        <button
          className={`w-full px-2 py-1 text-sm ${darkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white rounded`}
          onClick={() => setIsSelectingArtifacts(true)}
        >
          Select Artifacts
        </button>
      )}
      
      {isSelectingArtifacts && (
        <div className={`absolute z-10 mt-2 w-64 rounded-md shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} ring-1 ring-black ring-opacity-5`}>
          <div className={`p-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'} border-b`}>
            <input
              type="text"
              placeholder="Search artifacts..."
              className={`w-full p-1 text-sm rounded ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            
            {/* Selected artifacts display and Save button in header - use ref data for display */}
            {(artifactDataRef.current.selectedArtifacts.length > 0 || selectedArtifacts.length > 0) && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    Selected ({Math.max(artifactDataRef.current.selectedArtifacts.length, selectedArtifacts.length)})
                  </span>
                  <button
                    className={`px-3 py-1 text-xs ${darkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white rounded`}
                    onClick={handleSaveSelection}
                  >
                    Save Selection
                  </button>
                </div>
                <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                  {(artifactDataRef.current.selectedArtifacts.length > 0 ? artifactDataRef.current.selectedArtifacts : selectedArtifacts).map(artifact => (
                    <span 
                      key={artifact.id} 
                      className={`px-2 py-0.5 ${darkMode ? 'bg-purple-900 text-purple-100' : 'bg-purple-100 text-purple-800'} rounded-full text-xs flex items-center`}
                    >
                      {artifact.name}
                      <button 
                        className="ml-1 text-xs hover:text-red-500"
                        onClick={() => handleArtifactSelect(artifact.id)}
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="max-h-60 overflow-y-auto p-1">
            {loading ? (
              <div className="p-2 text-center">Loading artifacts...</div>
            ) : error ? (
              <div className="p-2 text-center text-red-500">{error}</div>
            ) : filteredArtifacts.length === 0 ? (
              <div className="p-2 text-center">No artifacts found</div>
            ) : (
              <>
                {filteredArtifacts.map(artifact => (
                  <div
                    key={artifact.id}
                    className={`p-2 cursor-pointer ${
                      selectedArtifacts.some(a => a.id === artifact.id)
                        ? darkMode ? 'bg-purple-900' : 'bg-purple-100'
                        : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                    }`}
                    onClick={() => handleArtifactSelect(artifact.id)}
                  >
                    <div className="font-medium">{artifact.name}</div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Type: {artifact.type}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtifactNode;
